import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Clock, MapPin, ChevronRight } from 'lucide-react';
import { fileUrl } from '@/lib/api';
import { useReveal } from '@/lib/motion';
import { sortSpeakersByLastName } from '@/lib/sortSpeakers';
import SectionBackground from '@/components/SectionBackground';

const FILTERS = ['ALL', 'SPORTS', 'MEDIA', 'INVESTMENT', 'INNOVATION', 'WELLNESS', 'HOSPITALITY', 'ACADEMIA'];

// v3.12 — Category filter UI hidden per stakeholder request. The tab row
// no longer renders, so the Speakers section displays ALL speakers by
// default (capped to HOMEPAGE_SPEAKER_LIMIT on the homepage, identical to
// the prior `ALL`-selected behavior). All filter LOGIC, the `FILTERS`
// constant, `categories` data on each speaker, and the tab JSX remain
// intact below — flipping this flag back to `true` restores the row
// exactly as it shipped in v3.11 with no other code changes required.
const SHOW_CATEGORY_FILTERS = false;

// Homepage caps the Speakers grid to a maximum number of cards per filter.
// The full uncapped directory lives at /speakers (reached via the
// "VIEW ALL SPEAKERS" CTA).
const HOMEPAGE_SPEAKER_LIMIT = 15;

// Word-based title truncation for the mobile speaker-card name line. If
// the speaker's name has more than `max` words, returns the first `max`
// words followed by "…"; otherwise returns the original string unchanged.
// Used on mobile only — desktop/tablet renders the full name.
const truncateWords = (text, max) => {
  if (!text) return '';
  const words = String(text).trim().split(/\s+/);
  if (words.length <= max) return text;
  return words.slice(0, max).join(' ') + '…';
};

export default function SpeakersSection({ speakers = [], featured = {}, section = {}, meta = {}, motion = {} }) {
  const [active, setActive] = useState('ALL');
  const navigate = useNavigate();
  const headerReveal = useReveal();
  const gridReveal = useReveal();
  const featuredReveal = useReveal();
  const cascadeEnabled = motion?.reveals?.speakers_cascade !== false;
  const featuredEnabled = motion?.reveals?.featured_session !== false;

  const eyebrow = meta.eyebrow || 'THE PEOPLE';
  const headline = meta.headline || 'SHAPING WHAT’S NEXT.';
  const buttonLabel = meta.button_label || 'View All Speakers';
  const buttonUrl = meta.button_url || '/speakers';

  const filtered = useMemo(() => {
    const base = active === 'ALL'
      ? speakers
      : speakers.filter((s) => (s.categories || []).map((c) => c.toUpperCase()).includes(active));
    // v3.16 (Round 2, 2C) — Alphabetical-by-last-name is a display-time
    // concern only.  The stored manual `order` is preserved on the
    // backend; here we sort every eligible speaker BEFORE the homepage
    // cap so the visible N are the alphabetically-first eligible
    // speakers, not the first N of the old manual ordering.
    return sortSpeakersByLastName(base).slice(0, HOMEPAGE_SPEAKER_LIMIT);
  }, [speakers, active]);

  const handleButton = () => {
    if (buttonUrl && buttonUrl.startsWith('http')) {
      window.open(buttonUrl, '_blank', 'noopener,noreferrer');
    } else if (buttonUrl && buttonUrl.startsWith('#')) {
      const el = document.querySelector(buttonUrl);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Synchronously persist the current scroll position so the POP-restore
      // handler in ScrollToTop can land the user back here on browser-back.
      try { sessionStorage.setItem('scrollPos:' + window.location.pathname, String(window.scrollY)); } catch (_) { /* ignore */ }
      navigate(buttonUrl || '/speakers');
    }
  };

  return (
    <SectionBackground section={section} fallbackColor="#F4EFE7">
      <section id="speakers" className="section-pad relative" data-testid="section-speakers">
        <div className="container-x relative z-10">
          <div ref={headerReveal.ref} className={`text-center ${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}>
            {/* v3.15 — Eyebrow size from sections_meta.speakers.eyebrow_size.
                Defensive validation: integer in [16, 48] only. Falls
                through to 26px default via CSS `var()` fallback. */}
            {(() => {
              const _raw = meta.eyebrow_size;
              const eyebrowSize = typeof _raw === 'number' && Number.isInteger(_raw) && _raw >= 16 && _raw <= 48 ? _raw : 26;
              return (
                <p className="eyebrow eyebrow-lg" data-testid="speakers-eyebrow" style={{ '--eyebrow-size': `${eyebrowSize}px` }}>{eyebrow}</p>
              );
            })()}
            <h2 className="mt-4 font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[36px] md:text-[40px] lg:text-[56px]" data-testid="speakers-headline">
              {headline}
            </h2>
          </div>

          {SHOW_CATEGORY_FILTERS && (
            <div className="relative mt-10">
              <div className="flex gap-2 overflow-x-auto no-scrollbar justify-start lg:justify-center pb-2 pr-12 md:pr-0" data-testid="speaker-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    onClick={() => setActive(f)}
                    className={
                      'shrink-0 font-body font-semibold text-[12px] uppercase tracking-wider2 px-[18px] py-[10px] rounded-full transition ' +
                      (active === f
                        ? 'bg-brand-orange text-white border border-brand-orange'
                        : 'bg-transparent text-ink-primary border border-border-line hover:border-ink-primary')
                    }
                    data-testid={`filter-${f.toLowerCase()}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="pointer-events-none absolute right-0 top-0 h-full flex items-center pl-3 pr-1 bg-gradient-to-l from-bg-canvas via-bg-canvas to-transparent md:hidden" aria-hidden="true">
                <ChevronRight className="w-5 h-5 text-ink-primary" />
              </div>
            </div>
          )}

          {/* v3.12 — Grid wrapper top margin compensates for the hidden
              filter row. When SHOW_CATEGORY_FILTERS is true, the filter
              wrapper above contributes `mt-10` (40px) and the grid uses
              `mt-8` (32px) — identical to the v3.11 layout. When the
              filter row is hidden, the grid alone absorbs the headline
              gap with `mt-12` (48px) for premium breathing room. */}
          <div ref={gridReveal.ref} className={`${SHOW_CATEGORY_FILTERS ? 'mt-8' : 'mt-12'} relative`}>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {filtered.map((s, i) => {
                const cols = 5;
                const row = Math.floor(i / cols);
                const colIdx = i % cols;
                const delay = cascadeEnabled ? row * 60 + colIdx * 30 : 0;
                return (
                  <SpeakerCard
                    key={s.id || i}
                    sp={s}
                    revealed={gridReveal.revealed}
                    simple={gridReveal.simple}
                    delay={delay}
                  />
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full text-center font-body text-ink-muted py-10">No speakers match this filter yet.</div>
              )}
            </div>
          </div>

          <div className="mt-10 text-center">
            <button className="btn-secondary" onClick={handleButton} data-testid="view-all-speakers">{buttonLabel}</button>
          </div>

          {featured && featured.title && (
            <div
              ref={featuredReveal.ref}
              className={`mt-12 ${featuredReveal.simple ? 'reveal-simple' : 'reveal-scale'} ${featuredReveal.revealed && featuredEnabled ? 'is-in' : (!featuredEnabled ? 'is-in' : '')}`}
            >
              <FeaturedSession featured={featured} />
            </div>
          )}
        </div>
      </section>
    </SectionBackground>
  );
}

function SpeakerCard({ sp, revealed, simple, delay }) {
  const photo = fileUrl(sp.photo) || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80&auto=format&fit=crop';
  const targetSlug = sp.slug || sp.id;

  return (
    <Link
      to={`/speakers/${targetSlug}`}
      onClick={() => {
        // v3.6 save-on-click: synchronously persist the current scroll
        // position so ScrollToTop's POP-restore handler can return the
        // user here on browser-back from the detail page.
        try { sessionStorage.setItem('scrollPos:' + window.location.pathname, String(window.scrollY)); } catch (_) { /* ignore */ }
      }}
      className={`group relative overflow-hidden rounded-md border border-border-line bg-card-dark hover:-translate-y-1 transition-transform duration-300 block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas ${simple ? 'reveal-simple' : 'reveal-card'} ${revealed ? 'is-in' : ''}`}
      style={{ aspectRatio: '3/4', transitionDelay: revealed ? `${delay}ms` : '0ms' }}
      data-testid="speaker-card"
    >
      <img src={photo} alt={sp.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[400ms] ease-out group-hover:scale-[1.04]" />

      <div className="absolute inset-x-0 bottom-0 translate-y-2 opacity-90 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-[300ms] ease-out">
        <div className="px-4 py-4" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.92) 60%, rgba(0,0,0,0))' }}>
          {/* Name — mobile: word-truncated + forced single line to prevent
              overflow onto the photo. Tapping the card navigates to the
              detail page via the parent <Link>. */}
          <div
            className="md:hidden font-display font-semibold uppercase text-white text-[14px] tracking-tightish truncate"
            data-testid="speaker-card-name-mobile"
          >
            {truncateWords(sp.name, 4)}
          </div>
          {/* Name — tablet/desktop: untouched (full text, identical to pre-fix). */}
          <div className="hidden md:block font-display font-semibold uppercase text-white text-[14px] tracking-tightish">
            {sp.name}
          </div>
          {/* Title — mobile: line-clamp-1 to prevent overflow. Desktop/tablet:
              line-clamp-none (full wrapping text, identical to pre-fix). */}
          <div
            className="font-body text-[11px] text-white/80 mt-1 line-clamp-1 md:line-clamp-none"
            data-testid="speaker-card-title"
          >
            {sp.title}
          </div>
        </div>
      </div>
    </Link>
  );
}

function FeaturedSession({ featured }) {
  const speakers = featured.speakers || [];
  const moderator = featured.moderator || null;
  return (
    <div className="bg-bg-elevated border border-border-line rounded-lg p-6 md:p-8" data-testid="featured-session">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div>
          <p className="font-display font-semibold uppercase text-brand-orange text-[12px] tracking-wider4">{featured.eyebrow || 'FEATURED SESSION'}</p>
          <p className="mt-3 font-display font-bold uppercase text-ink-primary text-[14px] tracking-tightish">{featured.label || 'OPENING KEYNOTE'}</p>
          <h3 className="mt-2 font-display font-bold uppercase text-ink-primary text-[26px] md:text-[26px] tracking-tightish leading-tight">{featured.title}</h3>
          <div className="mt-4 flex flex-wrap items-center gap-4 font-body font-medium text-[13px] text-ink-muted">
            <span className="inline-flex items-center gap-1.5"><Clock size={14} />{featured.datetime}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} />{featured.location}</span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-8">
          <div className="grid grid-cols-4 gap-4 flex-1">
            {speakers.slice(0, 4).map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <img src={fileUrl(s.photo)} alt={s.name} className="w-14 h-14 md:w-20 md:h-20 rounded-full object-cover border border-border-line" />
                <div className="mt-2 font-display font-semibold uppercase text-ink-primary text-[10px] md:text-[12px] tracking-tightish">{s.name}</div>
                <div className="font-body text-[10px] md:text-[11px] text-ink-soft">{s.title}</div>
              </div>
            ))}
          </div>

          {moderator && (
            <div className="flex flex-col items-center md:items-start md:border-l md:border-border-line md:pl-6">
              <p className="font-display font-semibold uppercase text-brand-orange text-[10px] tracking-wider4">Moderated By</p>
              <img src={fileUrl(moderator.photo)} alt={moderator.name} className="mt-2 w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border border-border-line" />
              <div className="mt-2 font-display font-semibold uppercase text-ink-primary text-[11px] md:text-[12px] tracking-tightish">{moderator.name}</div>
              <div className="font-body text-[10px] md:text-[11px] text-ink-soft">{moderator.title}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
